<script>
import axios from '@/utils/axios';

export default {
  name: 'InstallationModal',
  props: {
    editItem: {
      type: Object,
      default: null,
    },
  },
  emits: ['saved'],
  data() {
    return {
      form: {
        sales_order_id: null,
        delivery_challan_id: null,
        installation_person_id: null,
        status: 'pending',
      },
      errors: {},
      saving: false,

      // Dropdown options
      salesOrders: [],
      deliveryChallans: [],
      installationPersons: [],

      // Loaded flags - prevent repeated API calls
      soLoaded: false,
      dcLoaded: false,
      ipLoaded: false,

      // Search states
      soSearch: '',
      dcSearch: '',
      ipSearch: '',
      soTimer: null,
      dcTimer: null,
      ipTimer: null,
    };
  },
  mounted() {
    const modalEl = document.getElementById('installationModal');
    if (modalEl) {
      modalEl.addEventListener('show.bs.modal', () => {
        this.onModalOpen();
      });
    }
  },
  computed: {
    isEdit() {
      return !!this.editItem;
    },
    modalTitle() {
      return this.isEdit ? 'Edit Installation' : 'Add Installation';
    },
  },
  watch: {
    editItem(val) {
      if (val) {
        this.form = {
          sales_order_id: val.sales_order_id,
          delivery_challan_id: val.delivery_challan_id,
          installation_person_id: val.installation_person_id,
          status: val.status || 'pending',
        };
        // Pre-populate dropdown labels
        if (val.sales_order) {
          this.salesOrders = [{ id: val.sales_order_id, name: val.sales_order.name, so_number: val.sales_order.so_number }];
        }
        if (val.delivery_challan) {
          this.deliveryChallans = [{ id: val.delivery_challan_id, challan_number: val.delivery_challan.challan_number }];
        }
        if (val.installation_person) {
          this.installationPersons = [{ id: val.installation_person_id, name: val.installation_person.name }];
        }
      } else {
        this.resetForm();
      }
    },
  },
  methods: {
    resetForm() {
      this.form = {
        sales_order_id: null,
        delivery_challan_id: null,
        installation_person_id: null,
        status: 'pending',
      };
      this.errors = {};
      this.salesOrders = [];
      this.deliveryChallans = [];
      this.installationPersons = [];
      this.soLoaded = false;
      this.dcLoaded = false;
      this.ipLoaded = false;
      this.soSearch = '';
      this.dcSearch = '';
      this.ipSearch = '';
    },

    // ---- Sales Order search ----
    onSoSearch() {
      clearTimeout(this.soTimer);
      this.soTimer = setTimeout(() => this.fetchSalesOrders(this.soSearch), 350);
    },
    async fetchSalesOrders(search = '') {
      if (this.soLoaded && !search) return;
      try {
        const res = await axios.get('/sales-orders/dropdown/list', { params: { search } });
        this.salesOrders = res.data.data || [];
        if (!search) this.soLoaded = true;
      } catch (e) {
        console.error(e);
      }
    },

    // ---- Delivery Challan search ----
    onDcSearch() {
      clearTimeout(this.dcTimer);
      this.dcTimer = setTimeout(() => this.fetchDeliveryChallans(this.dcSearch), 350);
    },
    async fetchDeliveryChallans(search = '') {
      if (this.dcLoaded && !search) return;
      try {
        const res = await axios.get('/inventory/delivery-challans/dropdown', { params: { search } });
        this.deliveryChallans = res.data.data || [];
        if (!search) this.dcLoaded = true;
      } catch (e) {
        console.error(e);
      }
    },

    // ---- Installation Person search ----
    onIpSearch() {
      clearTimeout(this.ipTimer);
      this.ipTimer = setTimeout(() => this.fetchInstallationPersons(this.ipSearch), 350);
    },
    async fetchInstallationPersons(search = '') {
      if (this.ipLoaded && !search) return;
      try {
        const res = await axios.get('/installation-persons/dropdown', { params: { search } });
        const raw = res.data.data || [];
        this.installationPersons = raw.map(p => ({
          id: p.id,
          name: p.user?.name || 'Unknown',
          email: p.user?.email || '',
          mobile: p.user?.mobile || '',
        }));
        if (!search) this.ipLoaded = true;
      } catch (e) {
        console.error('Installation persons error:', e);
      }
    },

    // ---- Open modal → load initial options ----
    onModalOpen() {
      if (!this.isEdit) {
        this.resetForm();
      }
      this.fetchSalesOrders();
      this.fetchDeliveryChallans();
      this.fetchInstallationPersons();
    },

    // ---- Submit ----
    async submit() {
      this.errors = {};
      this.saving = true;
      try {
        if (this.isEdit) {
          await axios.patch(`/installations/${this.editItem.id}`, this.form);
          this.$swalToast('Installation updated successfully.');
        } else {
          await axios.post('/installations', this.form);
          this.$swalToast('Installation created successfully.');
        }
        this.$closeModal('installationModal');
        this.$emit('saved');
      } catch (e) {
        if (e?.errors) {
            this.errors = { ...e.errors };
        } else if (e?.response?.data?.errors) {
            this.errors = { ...e.response.data.errors };
        } else {
            this.$swalToast(e?.message || 'Something went wrong.', 'error');
        }
        } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="modal fade"
    id="installationModal"
    tabindex="-1"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">

        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">{{ modalTitle }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <div class="row g-3">

            <!-- Sales Order -->
            <div class="col-12">
              <label class="form-label">Sales Order <span class="text-danger">*</span></label>
              <vue-select
                v-model="form.sales_order_id"
                :options="salesOrders"
                :reduce="o => o.id"
                label="so_number"
                placeholder="Search sales order..."
                :filterable="false"
                @search="(q) => { soSearch = q; onSoSearch(); }"
              >
                <template #option="option">
                  <div>
                    <span class="fw-medium">{{ option.name }}</span>
                    <small class="text-muted ms-2">{{ option.so_number }}</small>
                  </div>
                </template>
                <template #selected-option="option">
                  {{ option.so_number }} — {{ option.name }}
                </template>
              </vue-select>
              <div v-if="errors.sales_order_id" class="text-danger small mt-1">
                {{ errors.sales_order_id[0] }}
              </div>
            </div>

            <!-- Delivery Challan -->
            <div class="col-12">
              <label class="form-label">Delivery Challan <span class="text-danger">*</span></label>
              <vue-select
                v-model="form.delivery_challan_id"
                :options="deliveryChallans"
                :reduce="o => o.id"
                label="challan_number"
                placeholder="Search delivery challan..."
                :filterable="false"
                @search="(q) => { dcSearch = q; onDcSearch(); }"
              >
                <template #option="option">
                  <div>
                    <span class="fw-medium">{{ option.challan_number }}</span>
                    <small v-if="option.recipient_details && option.recipient_details.name && option.recipient_details.name !== 'Loading...'" class="text-muted ms-2">
                      {{ option.recipient_details.name }}
                    </small>
                  </div>
                </template>
              </vue-select>
              <div v-if="errors.delivery_challan_id" class="text-danger small mt-1">
                {{ errors.delivery_challan_id[0] }}
              </div>
            </div>

            <!-- Installation Person -->
            <div class="col-12">
              <label class="form-label">Installation Manager <span class="text-danger">*</span></label>
              <vue-select
                v-model="form.installation_person_id"
                :options="installationPersons"
                :reduce="o => o.id"
                label="name"
                placeholder="Search installation manager..."
                :filterable="false"
                @search="(q) => { ipSearch = q; onIpSearch(); }"
              >
                <template #option="option">
                  <div>
                    <span class="fw-medium">{{ option.name }}</span>
                    <small v-if="option.email" class="text-muted ms-2">{{ option.email }}</small>
                  </div>
                </template>
              </vue-select>
              <div v-if="errors.installation_person_id" class="text-danger small mt-1">
                {{ errors.installation_person_id[0] }}
              </div>
            </div>

            <!-- Status (only on edit) -->
            <div class="col-12" v-if="isEdit">
              <label class="form-label">Status</label>
              <select class="form-select" v-model="form.status">
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <div v-if="errors.status" class="text-danger small mt-1">
                {{ errors.status[0] }}
              </div>
            </div>

          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button type="button" class="btn btn-light me-2" data-bs-dismiss="modal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="saving"
            @click="submit"
          >
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
            {{ isEdit ? 'Update' : 'Save' }}
          </button>
        </div>

      </div>
    </div>
  </div>
</template>